import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { fetchAllPaged } from '@/lib/fetchAllPaged'
import { fetchAllPagedIn } from '@/lib/inChunks'
import { POLL } from '@/lib/pollTiers'
import {
  sumRunPackaging, escapeLike, type MovementRow, type PalletIssue, type PackagingLine,
} from '@/lib/runPackaging'

interface Result { lines: (PackagingLine & { name: string; unit: string | null })[] }

const fail = (e: string | null) => { if (e) throw new Error(e) }
const fmt = (n: number) => n.toLocaleString('en-ZA', { maximumFractionDigits: 2 })

async function readPackaging(runId: string, runNumber: string): Promise<Result> {
  const pal = await fetchAllPaged<{ pallet_id: string }>((from, to) => supabase.from('pallets')
    .select('pallet_id').eq('run_id', runId).is('deleted_at', null).order('pallet_id').range(from, to))
  fail(pal.error)
  const palletIds = pal.rows.map(p => p.pallet_id)

  const iss = await fetchAllPagedIn<PalletIssue, string>(palletIds, (chunk, from, to) => supabase
    .from('pallet_stock_issues').select('pallet_id, stock_item_id, quantity')
    .in('pallet_id', chunk).order('issue_id').range(from, to))
  fail(iss.error)

  const auto = await fetchAllPagedIn<MovementRow, string>(palletIds, (chunk, from, to) => supabase
    .from('stock_movements').select('movement_id, item_id, quantity, reference')
    .eq('kind', 'ISSUE').eq('reason', 'Pallet pack (auto)').in('reference', chunk)
    .order('movement_id').range(from, to))
  fail(auto.error)

  const hand = await fetchAllPaged<MovementRow>((from, to) => supabase.from('stock_movements')
    .select('movement_id, item_id, quantity, reference').eq('kind', 'ISSUE')
    .ilike('reason', escapeLike(`Issued to run ${runNumber}`)).order('movement_id').range(from, to))
  fail(hand.error)

  const ids = [...auto.rows, ...hand.rows].map(m => m.movement_id)
  const voids = await fetchAllPagedIn<{ reference: string | null }, string>(
    ids.map(i => `void:${i}`),
    (chunk, from, to) => supabase.from('stock_movements').select('reference')
      .in('reference', chunk).order('movement_id').range(from, to))
  fail(voids.error)
  const voided = new Set<string>()
  for (const v of voids.rows) if (v.reference) voided.add(v.reference.slice(5))

  const lines = sumRunPackaging(iss.rows, auto.rows, hand.rows, voided)
  const names = await fetchAllPagedIn<{ item_id: string; item: string; base_unit: string | null }, string>(
    lines.map(l => l.item_id),
    (chunk, from, to) => supabase.from('stock_items').select('item_id, item, base_unit')
      .in('item_id', chunk).order('item_id').range(from, to))
  fail(names.error)
  const byId = new Map(names.rows.map(n => [n.item_id, n]))
  return {
    lines: lines
      .map(l => ({ ...l, name: byId.get(l.item_id)?.item ?? 'Unknown item', unit: byId.get(l.item_id)?.base_unit ?? null }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  }
}

/** UF-01: "Packaging used" on the run's Overview. Self-reading, units only
 *  (never rands). A failed read says so; it never shows "none used". */
export function RunPackagingCard({ runId, runNumber }: { runId: string; runNumber: string }) {
  const q = useQuery({
    queryKey: ['run-packaging', runId, runNumber],
    refetchInterval: POLL.standard,
    queryFn: () => readPackaging(runId, runNumber),
  })
  if (q.isError) {
    return <div className="app-card p-3 text-caption text-fg-muted" data-testid="run-packaging">Could not read packaging. Try again in a moment.</div>
  }
  if (!q.data) return null
  if (q.data.lines.length === 0) {
    return <div className="app-card p-3 text-caption text-fg-muted" data-testid="run-packaging">No packaging recorded for this run</div>
  }
  return (
    <div className="app-card p-3 space-y-2" data-testid="run-packaging">
      <p className="text-body font-semibold text-fg">Packaging used</p>
      <div className="overflow-x-auto">
        <table className="w-full text-caption">
          <thead>
            <tr className="text-fg-muted text-left">
              <th className="font-medium py-1 pr-2">Item</th>
              <th className="font-medium py-1 px-2 text-right">On pallets</th>
              <th className="font-medium py-1 px-2 text-right">By hand</th>
              <th className="font-medium py-1 pl-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {q.data.lines.map(l => (
              <tr key={l.item_id} className="border-t border-line-subtle text-fg">
                <td className="py-1 pr-2">{l.name}{l.unit ? <span className="text-fg-muted"> · {l.unit}</span> : null}</td>
                <td className="py-1 px-2 text-right tabular-nums">{fmt(l.onPallets)}</td>
                <td className="py-1 px-2 text-right tabular-nums">{fmt(l.byHand)}</td>
                <td className="py-1 pl-2 text-right tabular-nums font-semibold">{fmt(l.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-caption text-fg-muted">
        By hand = issues whose reason names {runNumber}. Units only. Deleted pallets and voided issues are left out; build-up pallets count in full on their own run.
      </p>
    </div>
  )
}
